import type {
  DigestedSchema,
  EditorSDK,
} from '@wix/wix-data-client-common-standalone'
import type { I18nextProviderProps } from 'react-i18next'
import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import { has } from 'lodash'
import { getConnectedField } from './utils'
import { userInputFilterConditionOptions } from '@wix/wix-data-client-common'
import type {
  BindingConfig,
  FilterBindingConfig,
} from '@wix/wix-data-client-common'

type ComponentData = Record<string, unknown> | undefined
type ComponentProps = Record<string, unknown> | undefined

type PropsOverride = {
  props: Record<string, unknown>
}

type DataOverride = {
  data: Record<string, unknown>
}

type ComponentOverride = PropsOverride | DataOverride | void

export const updateComponentLabelAndPlaceholder = async ({
  collections,
  componentData,
  controllerRef,
  editorSdkProxy,
  i18n,
  newBindings,
}: {
  collections: DigestedSchema[]
  componentData: ComponentData
  componentProperties: ComponentProps
  controllerRef: ComponentRef
  editorSdkProxy: EditorSDK
  i18n: I18nextProviderProps['i18n']
  newBindings: BindingConfig[]
}): Promise<ComponentOverride> => {
  const field = await getConnectedField({
    editorSdkProxy,
    controllerRef,
    collections,
    newBindings,
  })

  if (field?.label) {
    const componentHasLabelInData = has(componentData, 'label')
    const componentHasPlaceholder = has(componentData, 'placeholder')

    const condition = (newBindings[0] as FilterBindingConfig)?.condition

    const { label, placeholder } =
      userInputFilterConditionOptions.find(
        o => o.condition === condition && o.fieldType === field.type,
      ) || userInputFilterConditionOptions[0]

    const newLabel = i18n.t(label, {
      fieldLabel: field.label,
    })
    const newPlaceholder = placeholder
      ? i18n.t(placeholder, {
          fieldLabel: field.label,
        })
      : null

    if (componentHasLabelInData || componentHasPlaceholder) {
      return {
        data: {
          ...(componentHasLabelInData && { label: newLabel }),
          ...(componentHasPlaceholder && {
            placeholder: {
              value: newPlaceholder,
              text: newPlaceholder,
            },
            value: null,
          }),
        },
      }
    } else {
      return {
        props: {
          label: newLabel,
        },
      }
    }
  }
}

export const uncheckComponentsCheckedProperty = ({
  componentData,
}: {
  componentData: ComponentData
}): ComponentOverride => {
  if (componentData?.checked) {
    return {
      data: {
        checked: false,
      },
    }
  }
}

export const markComponentNonRequired = ({
  componentProperties,
}: {
  componentProperties: ComponentProps
}): ComponentOverride => {
  if (componentProperties?.required) {
    return {
      props: {
        required: false,
      },
    }
  }
}

export const markComponentNonReadOnly = ({
  componentData,
}: {
  componentData: ComponentData
}): ComponentOverride => {
  if (componentData?.readOnly) {
    return {
      data: {
        readOnly: false,
      },
    }
  }
}

export const overrideRangeStepsDefaults = ({
  componentData,
}: {
  componentData: ComponentData
}): ComponentOverride => {
  if (
    componentData?.sliderType === 'Continuous' &&
    componentData?.stepType === 'Number' &&
    componentData?.step === 1
  ) {
    return {
      data: {
        sliderType: 'Stepped',
        stepType: 'Number',
        step: 20,
      },
    }
  }
}
